import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { useOpsPhysicalInventorySettings } from "@/components/ops/useOpsPhysicalInventorySettings";

/**
 * Ops → Physical inventory (PI-1): org cadence + photo slot config shell.
 * Field capture lands in Fulfillment at PI-3; this mode stays policy/settings.
 */
export function OpsPhysicalInventoryPanel() {
  const organizationId = useScopedOrganizationId();
  const vm = useOpsPhysicalInventorySettings(organizationId);

  if (!organizationId) {
    return (
      <div className="w-full text-left space-y-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Physical inventory</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">Select a workspace organization first.</p>
      </div>
    );
  }

  const slotDisabled = (slot: 1 | 2 | 3) => vm.settings.photos_required < slot;

  return (
    <div className="w-full text-left space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Physical inventory</h2>
        <p className="text-sm text-gray-600 dark:text-zinc-300 mt-1 max-w-3xl">
          Lot unit verification against finished inventory (mfg_units) — cadence, photo requirements,
          and (later) exception review. Field capture ships in Fulfillment; this Ops surface owns policy.
        </p>
      </div>

      {vm.error ? (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {vm.error}
        </p>
      ) : null}
      {vm.loading ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Loading settings…</p>
      ) : (
        <>
          <section className="space-y-3 max-w-xl">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Cadence</h3>
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-200">
              <input
                type="checkbox"
                checked={vm.settings.enabled}
                onChange={(e) => vm.updateField("enabled", e.target.checked)}
              />
              Enable physical inventory for this org
            </label>
            <label className="block text-sm text-gray-700 dark:text-zinc-300">
              Cadence
              <select
                className="mt-1 block w-full rounded border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5"
                value={vm.settings.cadence}
                onChange={(e) =>
                  vm.updateField("cadence", e.target.value as "monthly" | "weekly" | "none")
                }
              >
                <option value="none">Ad hoc only</option>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
              </select>
            </label>
            <label className="block text-sm text-gray-700 dark:text-zinc-300">
              Due day of month
              <select
                className="mt-1 block w-full rounded border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5"
                value={vm.settings.due_day_of_month}
                onChange={(e) => vm.updateField("due_day_of_month", e.target.value)}
              >
                <option value="last">Last calendar day</option>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={String(d)}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm text-gray-700 dark:text-zinc-300">
              Notify days before due
              <input
                type="number"
                min={0}
                max={31}
                className="mt-1 block w-full rounded border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5"
                value={vm.settings.notify_days_before}
                onChange={(e) => vm.updateField("notify_days_before", Number(e.target.value) || 0)}
              />
            </label>
            <label className="block text-sm text-gray-700 dark:text-zinc-300">
              Default assignees
              <select
                className="mt-1 block w-full rounded border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5"
                value={vm.settings.assignee_mode}
                onChange={(e) =>
                  vm.updateField(
                    "assignee_mode",
                    e.target.value as "location_members" | "org_wide_sales"
                  )
                }
              >
                <option value="location_members">Location members</option>
                <option value="org_wide_sales">Org-wide sales</option>
              </select>
            </label>
          </section>

          <section className="space-y-3 max-w-xl">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Photo slots</h3>
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              Bennett default: 2 photos — exterior context + serial plate. Field upload arrives in PI-3.
            </p>
            <label className="block text-sm text-gray-700 dark:text-zinc-300">
              Photos required (0–3)
              <input
                type="number"
                min={0}
                max={3}
                className="mt-1 block w-full rounded border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5"
                value={vm.settings.photos_required}
                onChange={(e) => {
                  const n = Math.max(0, Math.min(3, Number(e.target.value) || 0));
                  vm.updateField("photos_required", n);
                }}
              />
            </label>
            {(
              [
                ["photo_slot_1_label", 1, "Slot 1 label"],
                ["photo_slot_2_label", 2, "Slot 2 label"],
                ["photo_slot_3_label", 3, "Slot 3 label"],
              ] as const
            ).map(([key, slot, label]) => (
              <label key={key} className="block text-sm text-gray-700 dark:text-zinc-300">
                {label}
                <input
                  type="text"
                  disabled={slotDisabled(slot)}
                  className="mt-1 block w-full rounded border border-gray-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 px-2 py-1.5 disabled:opacity-50"
                  value={vm.settings[key]}
                  onChange={(e) => vm.updateField(key, e.target.value)}
                />
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-200">
              <input
                type="checkbox"
                checked={vm.settings.require_serial_readable}
                onChange={(e) => vm.updateField("require_serial_readable", e.target.checked)}
              />
              Soft flag: require serial readable (manual confirm in V1)
            </label>
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={vm.saving}
              onClick={() => void vm.save()}
              className="rounded bg-gray-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 text-sm font-medium disabled:opacity-60"
            >
              {vm.saving ? "Saving…" : "Save settings"}
            </button>
            {vm.savedAt ? (
              <span className="text-xs text-gray-500 dark:text-zinc-400">Saved</span>
            ) : null}
          </div>

          <p className="text-xs text-gray-500 dark:text-zinc-400 max-w-3xl">
            Period open, expected-unit snapshot, and field verify come in PI-2 / PI-3. This mode stays
            visible even with empty lot inventory.
          </p>
        </>
      )}
    </div>
  );
}
